import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, RotateCcw, BookOpen, CheckCircle, Lightbulb } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

// Whiteboard-style animated presentation component
export default function InteractivePresentation({ lesson, onComplete }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [completedSlides, setCompletedSlides] = useState([]);

  const slides = lesson?.slides || [];
  const slide = slides[currentSlide];

  useEffect(() => {
    if (isPlaying && slide) {
      const phases = 4; // title, content, key points, illustration
      const timer = setInterval(() => {
        setAnimationPhase((prev) => {
          if (prev >= phases) {
            if (currentSlide < slides.length - 1) {
              setCurrentSlide((c) => c + 1);
              return 0;
            } else {
              setIsPlaying(false);
              return prev;
            }
          }
          return prev + 1;
        });
      }, 2000);
      return () => clearInterval(timer);
    }
  }, [isPlaying, currentSlide, slides.length, slide]);

  useEffect(() => {
    setAnimationPhase(0);
    if (!completedSlides.includes(currentSlide)) {
      setCompletedSlides([...completedSlides, currentSlide]);
    }
  }, [currentSlide]);

  const progress = ((currentSlide + 1) / slides.length) * 100;

  const goNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
      setAnimationPhase(0);
    } else if (onComplete) {
      onComplete();
    }
  };

  const goPrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
      setAnimationPhase(0);
    }
  };

  const resetPresentation = () => {
    setCurrentSlide(0);
    setAnimationPhase(0);
    setIsPlaying(false);
    setCompletedSlides([]);
  };

  if (!lesson || !slides.length) {
    return (
      <div className="card-sketchy text-center py-16">
        <BookOpen className="w-20 h-20 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-sketch text-2xl">No lesson content</h3>
        <p className="text-muted-foreground">Generate a lesson first</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-sketch text-2xl text-ink">{lesson.lesson_title}</h2>
          <p className="text-muted-foreground">{lesson.lesson_description}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{lesson.estimated_duration_minutes} min</span>
        </div>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-4">
        <Progress value={progress} className="h-2 flex-1" />
        <span className="text-sm font-semibold">{currentSlide + 1}/{slides.length}</span>
      </div>

      {/* Whiteboard Canvas */}
      <div className="relative bg-white border-4 border-black rounded-xl overflow-hidden shadow-hard-lg" style={{ minHeight: '500px' }}>
        {/* Whiteboard background pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} />

        {/* Slide Content */}
        <div className="relative p-8 space-y-6">
          {/* Slide Number Badge */}
          <div className="absolute top-4 right-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg border-2 border-black">
            {slide.slide_number || currentSlide + 1}
          </div>

          {/* Title with draw-in animation */}
          <div className={`transition-all duration-700 ${animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
            <h3 className="font-sketch text-4xl text-ink border-b-4 border-yellow-400 pb-2 inline-block">
              {slide.title}
            </h3>
          </div>

          {/* Main Content with typewriter effect */}
          <div className={`transition-all duration-700 delay-300 ${animationPhase >= 2 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
            <div className="bg-gray-50 border-2 border-black rounded-lg p-6 text-lg leading-relaxed">
              {slide.content}
            </div>
          </div>

          {/* Key Points with staggered animation */}
          <div className={`transition-all duration-700 delay-500 ${animationPhase >= 3 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              <span className="font-sketch text-xl text-ink">Key Takeaways</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {slide.key_points?.map((point, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-3 bg-yellow-50 border-2 border-yellow-400 rounded-lg"
                  style={{ animationDelay: `${idx * 200}ms` }}
                >
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm font-medium">{point}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Illustration Description */}
          {slide.illustration_description && (
            <div className={`transition-all duration-700 delay-700 ${animationPhase >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg border-dashed">
                <p className="text-sm text-blue-700 italic">
                  📝 Visual: {slide.illustration_description}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Animated character/teacher indicator */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-white px-3 py-2 rounded-full border-2 border-black">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold">SketchLearn AI</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={resetPresentation}
            className="border-2 border-black"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setIsPlaying(!isPlaying)}
            className="border-2 border-black"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={goPrev}
            disabled={currentSlide === 0}
            className="border-2 border-black"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <Button
            className="btn-sketchy"
            onClick={goNext}
          >
            {currentSlide === slides.length - 1 ? 'Complete' : 'Next'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Learning Objectives */}
      {lesson.learning_objectives && (
        <div className="card-sketchy bg-green-50">
          <h4 className="font-sketch text-lg text-ink mb-3">🎯 Learning Objectives</h4>
          <ul className="space-y-2">
            {lesson.learning_objectives.map((obj, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500" />
                {obj}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Slide Navigation Dots */}
      <div className="flex items-center justify-center gap-2">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => { setCurrentSlide(idx); setAnimationPhase(0); }}
            className={`w-3 h-3 rounded-full transition-all ${
              idx === currentSlide
                ? 'bg-primary scale-125'
                : completedSlides.includes(idx)
                ? 'bg-green-500'
                : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
